<template>
  <Teleport to="body">
    <audio
      ref="ringtoneEl"
      :src="incomingRingtoneUrl"
      loop
      preload="auto"
      style="display: none;"
    />
    <Transition name="incoming-call-fade">
      <div v-if="shouldShowModal" class="incoming-call-overlay" @click.self.stop>
        <div class="incoming-call-modal">
          <!-- Call type badge -->
          <div class="incoming-call-badge">
            <VsxIcon :iconName="isVideo ? 'Video' : 'Call'" :size="18" type="bold" class="incoming-call-badge-icon" />
            <span v-if="needsIncomingDecision">{{ $t('chat.incomingCallUnresolvedTitle') }}</span>
            <span v-else-if="isOngoingElsewhere">{{ $t('chat.activeCall') || 'Active Call' }}</span>
            <span v-else>{{ isVideo ? $t('chat.incomingVideoCall') : $t('chat.incomingVoiceCall') }}</span>
          </div>

          <!-- Avatar with ringing animation -->
          <div class="incoming-call-avatar-wrap">
            <div class="incoming-call-avatar-rings" aria-hidden="true" v-if="!isOngoingElsewhere && !needsIncomingDecision">
              <span class="ring ring-1"></span>
              <span class="ring ring-2"></span>
              <span class="ring ring-3"></span>
            </div>
            <div class="incoming-call-avatar">
              <img v-if="callStore.remoteAvatarUrl" :src="callStore.remoteAvatarUrl" alt=""
                class="incoming-call-avatar-img" @error="($event.target).src = defaultAvatar" />
              <VsxIcon v-else iconName="User" :size="56" type="bold" class="incoming-call-avatar-fallback" />
            </div>
          </div>

          <p class="incoming-call-name" :class="{ 'incoming-call-name--with-hint': needsIncomingDecision }">
            {{ callStore.remoteDisplayName || $t('chat.caller') }}
          </p>
          <p v-if="needsIncomingDecision" class="incoming-call-hint">{{ $t('chat.incomingCallUnresolvedHint') }}</p>

          <!-- Action buttons with labels -->
          <div class="incoming-call-actions">
            <template v-if="needsIncomingDecision">
              <button type="button" class="incoming-btn incoming-btn--reject" @click="callStore.dismissPendingIncomingCall()"
                :aria-label="$t('chat.closeIncomingCall')">
                <span class="incoming-btn-circle">
                  <VsxIcon iconName="CloseCircle" :size="28" type="bold" color="#fff" />
                </span>
                <span class="incoming-btn-label">{{ $t('chat.closeIncomingCall') }}</span>
              </button>
              <button type="button" class="incoming-btn incoming-btn--accept" @click="callStore.rejoinPendingIncomingCall()"
                :aria-label="$t('chat.rejoinCall')">
                <span class="incoming-btn-circle incoming-btn-circle--accept">
                  <VsxIcon :iconName="isVideo ? 'Video' : 'Call'" :size="28" type="bold" color="#fff" />
                </span>
                <span class="incoming-btn-label">{{ $t('chat.rejoinCall') }}</span>
              </button>
            </template>
            <template v-else-if="isOngoingElsewhere">
              <button type="button" class="incoming-btn incoming-btn--reject" @click="endOngoingActiveCall()"
                :aria-label="$t('chat.endActiveCall')">
                <span class="incoming-btn-circle">
                  <VsxIcon iconName="CloseCircle" :size="28" type="bold" color="#fff" />
                </span>
                <span class="incoming-btn-label">{{ $t('chat.endActiveCall') }}</span>
              </button>
              <button type="button" class="incoming-btn incoming-btn--accept" @click="callStore.joinOngoingCall()"
                :aria-label="$t('chat.rejoinCall')">
                <span class="incoming-btn-circle incoming-btn-circle--accept">
                  <VsxIcon :iconName="isVideo ? 'Video' : 'Call'" :size="28" type="bold" color="#fff" />
                </span>
                <span class="incoming-btn-label">{{ $t('chat.rejoinCall') }}</span>
              </button>
            </template>
            <template v-else>
              <button type="button" class="incoming-btn incoming-btn--reject" @click="callStore.rejectCall()"
                :aria-label="$t('chat.rejectCall')">
                <span class="incoming-btn-circle">
                  <VsxIcon iconName="CloseCircle" :size="28" type="bold" color="#fff" />
                </span>
                <span class="incoming-btn-label">{{ $t('chat.rejectCall') }}</span>
              </button>
              <button type="button" class="incoming-btn incoming-btn--accept" @click="callStore.acceptCall()"
                :aria-label="$t('chat.acceptCall')">
                <span class="incoming-btn-circle incoming-btn-circle--accept">
                  <VsxIcon :iconName="isVideo ? 'Video' : 'Call'" :size="28" type="bold" color="#fff" />
                </span>
                <span class="incoming-btn-label">{{ $t('chat.acceptCall') }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { VsxIcon } from 'vue-iconsax';
import { useCallStore } from '@/domains/chat/store/callStore';
import { displayImage } from '@/shared/utils/generalUtils';
import incomingRingtoneUrl from '@/assets/sounds/incoming_call.mp3';

const callStore = useCallStore();
const isVideo = computed(() => callStore.isVideoCall);
const defaultAvatar = displayImage(null, 'user');

const isOngoingElsewhere = computed(() => callStore.callState === callStore.CALL_STATES.ONGOING_ELSEWHERE);
const needsIncomingDecision = computed(
  () =>
    callStore.callState === callStore.CALL_STATES.INCOMING_UNRESOLVED &&
    Boolean(callStore.session?.isIncoming),
);
const shouldShowModal = computed(
  () => callStore.hasIncomingCall || isOngoingElsewhere.value || needsIncomingDecision.value,
);

/** Match mobile restore banner: end the call on the server, not only dismiss local UI. */
const endOngoingActiveCall = () => {
  callStore.endCall().catch((e) => console.warn('[IncomingCallModal] endCall failed', e));
};

const ringtoneEl = ref(null);
const isRinging = computed(() => callStore.hasIncomingCall);

watch(isRinging, async (ringing) => {
  const el = ringtoneEl.value;
  if (!el) return;

  if (ringing) {
    el.currentTime = 0;
    const maybePromise = el.play && el.play();
    if (maybePromise && typeof maybePromise.then === 'function') {
      try {
        await maybePromise;
      } catch {
        // Autoplay may be blocked on some mobile browsers; rely on visual UI in that case.
      }
    }
  } else {
    if (el.pause) {
      el.pause();
    }
    el.currentTime = 0;
  }
});

onUnmounted(() => {
  const el = ringtoneEl.value;
  if (!el) return;
  if (el.pause) {
    el.pause();
  }
  el.currentTime = 0;
});
</script>

<style scoped>
.incoming-call-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.5rem;
}

.incoming-call-modal {
  background: var(--color-bg-secondary, #ffffff);
  min-width: 360px;
  max-width: 90vw;
  border-radius: 2rem;
  padding: 3.5rem 2.5rem;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.3),
    0 4px 20px rgba(0, 0, 0, 0.05),
    inset 0 0 0 1px var(--color-border, rgba(0, 0, 0, 0.05));
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: incoming-call-drop-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

/* Subtle top gradient bar for premium feel */
.incoming-call-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, var(--color-brand), var(--color-brand-soft), var(--color-brand));
  opacity: 0.9;
}

@keyframes incoming-call-drop-in {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.incoming-call-fade-enter-active,
.incoming-call-fade-leave-active {
  transition: opacity 0.3s ease;
}
.incoming-call-fade-enter-from,
.incoming-call-fade-leave-to {
  opacity: 0;
}
.incoming-call-fade-enter-active .incoming-call-modal,
.incoming-call-fade-leave-active .incoming-call-modal {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.incoming-call-fade-enter-from .incoming-call-modal,
.incoming-call-fade-leave-to .incoming-call-modal {
  transform: scale(0.9) translateY(10px);
}

/* Call type badge */
.incoming-call-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text-muted, #64748b);
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  background: var(--color-bg-tertiary, #f1f5f9);
  border: 1px solid var(--color-border, rgba(0, 0, 0, 0.05));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.incoming-call-badge-icon {
  flex-shrink: 0;
  color: var(--color-brand);
}

/* Avatar with rings */
.incoming-call-avatar-wrap {
  position: relative;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.incoming-call-avatar-rings {
  position: absolute;
  inset: -24px;
  pointer-events: none;
}

.ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid var(--color-brand);
  animation: incoming-call-ring 2.5s cubic-bezier(0.21, 0.53, 0.56, 1) infinite;
  opacity: 0;
}

.ring-1 { animation-delay: 0s; }
.ring-2 { animation-delay: 0.8s; }
.ring-3 { animation-delay: 1.6s; }

@keyframes incoming-call-ring {
  0% {
    transform: scale(0.85);
    opacity: 0.6;
    border-width: 2px;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
    border-width: 0px;
  }
}

.incoming-call-avatar {
  position: relative;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--color-bg-tertiary, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--color-bg-secondary, #fff);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.incoming-call-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.incoming-call-avatar-fallback {
  color: var(--color-brand);
}

.incoming-call-name {
  font-size: 1.65rem;
  font-weight: 800;
  color: var(--color-text, #0f172a);
  margin-bottom: 3rem;
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.incoming-call-name--with-hint {
  margin-bottom: 1rem;
}

.incoming-call-hint {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--color-text-muted, #64748b);
  margin: 0 0 2rem;
  line-height: 1.5;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
}

/* BRAND NEW ACTION BUTTONS - CAPSULE STYLE */
.incoming-call-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-top: 0.5rem;
}

.incoming-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem 2rem;
  border-radius: 100px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.incoming-btn:active {
  transform: scale(0.96);
}

.incoming-btn:focus-visible {
  outline: 3px solid var(--color-brand);
  outline-offset: 4px;
}

/* Hide the old circles and just rely on the new pills */
/* Show the icons inside the new pills */
.incoming-btn-circle {
  display: flex !important;
  align-items: center;
  justify-content: center;
}
.incoming-btn-circle--accept {
  display: flex !important;
  align-items: center;
  justify-content: center;
}

/* Accept Button - Green Glowing Pill */
.incoming-btn--accept {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white !important;
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
  animation: pulse-button 2s infinite cubic-bezier(0.4, 0, 0.6, 1);
}

.incoming-btn--accept .incoming-btn-label {
  color: white !important;
}

@keyframes pulse-button {
  0%, 100% { box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3); }
  50% { box-shadow: 0 12px 30px rgba(16, 185, 129, 0.6); }
}

.incoming-btn--accept:hover {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  animation: none;
  box-shadow: 0 15px 35px rgba(16, 185, 129, 0.4);
  transform: translateY(-4px);
}

/* Reject Button - Red Outline / Ghost Pill */
.incoming-btn--reject {
  background: var(--color-bg-secondary, #fff);
  color: #ef4444 !important;
  border: 2px solid #fee2e2;
}

.incoming-btn--reject .incoming-btn-label {
  color: #ef4444 !important;
}

.incoming-btn--reject:hover {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #dc2626 !important;
  box-shadow: 0 10px 20px rgba(239, 68, 68, 0.1);
  transform: translateY(-4px);
}

.incoming-btn--reject:hover .incoming-btn-label {
  color: #dc2626 !important;
}

/* Dark mode — reject button */
[data-bs-theme="dark"] .incoming-btn--reject,
html[data-bs-theme="dark"] .incoming-btn--reject {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
  color: #f87171 !important;
}
[data-bs-theme="dark"] .incoming-btn--reject .incoming-btn-label {
  color: #f87171 !important;
}

[data-bs-theme="dark"] .incoming-btn--reject:hover,
html[data-bs-theme="dark"] .incoming-btn--reject:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.4);
}
</style>
